// WIDGET 6 — Application Compliance Score: Compliance % (Passed/Assessed) + Coverage % (Assessed/Total), assessment-based.
// Self-fetching: calls its own dedicated endpoint
// (GET /dashboard/grc/system-owner/control-compliance) rather than a shared rollup.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Info, ArrowRight, AlertTriangle } from "lucide-react";
import { Card, SectionTitle } from "../../GRCManagerDashboard/grcDashboardWidgets";
import { DataTable, type Column } from "../../../components/DataTable";
import { Tip } from "../../../components/ui/tooltip";
import { Sheet, SheetContent } from "../../../components/ui/sheet";
import { EmptyState } from "../../../components/EmptyState";
import { SkeletonList } from "../../../components/Skeleton";
import {
  NOT_IDENTIFIED_HEX,
  STATUS_HEX,
  rateBandHex,
  buildApplicationCompliance,
  type ApplicationComplianceSummary,
  type SspComplianceSummary,
} from "../data/applicationKriData";
import { fetchControlCompliance, type SystemOwnerControlComplianceResponse } from "../../../../api/frameworksApi";

// Shared by the app table and SSP drill-down so colors stay consistent
function StatusLegend() {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
      <span className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
        <span className="size-2.5 shrink-0 rounded-full" style={{ background: STATUS_HEX.good }} /> Satisfied
      </span>
      <span className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
        <span className="size-2.5 shrink-0 rounded-full" style={{ background: STATUS_HEX.critical }} /> Not Satisfied
      </span>
      <span className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
        <span className="size-2.5 shrink-0 rounded-full" style={{ background: NOT_IDENTIFIED_HEX }} /> Not Reviewed (plan open, review not yet complete)
      </span>
    </div>
  );
}

// Passed / Failed / Not Identified as a share of the control total
function StatusStackBar({ passed, failed, notIdentified }: { passed: number; failed: number; notIdentified: number }) {
  const total = Math.max(1, passed + failed + notIdentified);
  return (
    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-foreground/[0.06]">
      <div style={{ width: `${(passed / total) * 100}%`, background: STATUS_HEX.good }} />
      <div style={{ width: `${(failed / total) * 100}%`, background: STATUS_HEX.critical }} />
      <div style={{ width: `${(notIdentified / total) * 100}%`, background: NOT_IDENTIFIED_HEX }} />
    </div>
  );
}

// Same rate-band coloring used elsewhere on this page (high = good)
function CompliancePctBadge({ pct }: { pct: number }) {
  const hex = rateBandHex(pct / 100, "compliance");
  return (
    <span className="inline-flex items-center rounded-md px-2 py-0.5 text-sm font-bold tabular-nums" style={{ background: `${hex}1f`, color: hex }}>
      {pct.toFixed(1)}%
    </span>
  );
}

function SspDetailSheet({
  open,
  onOpenChange,
  app,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  app: ApplicationComplianceSummary | null;
}) {
  const columns: Column<SspComplianceSummary>[] = [
    {
      key: "sspName",
      header: "SSP",
      align: "left",
      headerAlign: "left",
      render: (s) => (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <p className="text-sm font-semibold text-foreground">{s.sspName}</p>
          {s.shortCode && (
            <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-foreground/70">
              {s.shortCode}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "breakdown",
      header: "Satisfied / Not Satisfied / Not Reviewed",
      align: "left",
      headerAlign: "left",
      render: (s) => (
        <div className="min-w-[160px] space-y-1.5">
          <StatusStackBar passed={s.passed} failed={s.failed} notIdentified={s.notIdentified} />
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold" style={{ color: STATUS_HEX.good }}>{s.passed} satisfied</span>
            {" · "}
            <span className="font-semibold" style={{ color: STATUS_HEX.critical }}>{s.failed} not satisfied</span>
            {" · "}
            <span className="font-semibold" style={{ color: NOT_IDENTIFIED_HEX }}>{s.notIdentified} not reviewed</span>
            {" · "}
            {s.coveredControls} of {s.totalControls} total controls
          </p>
        </div>
      ),
    },
    {
      key: "coverage",
      header: "Coverage",
      render: (s) => <span className="text-sm font-semibold tabular-nums text-foreground">{s.coveragePercent.toFixed(1)}%</span>,
    },
    {
      key: "compliance",
      header: "Compliance",
      render: (s) => <CompliancePctBadge pct={s.compliancePercent} />,
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex h-full max-h-screen w-[96vw] flex-col gap-0 overflow-hidden border-l border-border bg-background p-0 sm:max-w-[820px]">
        {app && (
          <div key={app.applicationName} className="flex h-full min-h-0 flex-1 flex-col animate-in fade-in-0 slide-in-from-right-4 duration-300 ease-out">
            <div className="shrink-0 border-b border-border p-6">
              <h2 className="text-xl font-bold text-foreground">{app.applicationName}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {app.sspCount === 0
                  ? "No SSP published yet for this application."
                  : `${app.sspCount} SSP${app.sspCount === 1 ? "" : "s"} — compliance shown per SSP below; the tile on the dashboard is the sum across all of them.`}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
                <span>Coverage: <b className="text-foreground">{app.coveragePercent.toFixed(1)}%</b></span>
                <span>Compliance: <CompliancePctBadge pct={app.compliancePercent} /></span>
                <span>Not Reviewed: <b className="text-foreground">{app.notIdentified}</b> controls</span>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <StatusLegend />
              <DataTable columns={columns} data={app.ssps} rowKey={(s) => s.sspId} emptyText="No SSPs on this application" hidePageButtons compact />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const TOP_N = 5;

export function ApplicationComplianceWidget() {
  const [selected, setSelected] = useState<ApplicationComplianceSummary | null>(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [data, setData] = useState<SystemOwnerControlComplianceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchControlCompliance()
      .then((res) => setData(res?.data ?? null))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load compliance scores"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const apps = useMemo(() => buildApplicationCompliance(data?.applications ?? []), [data]);

  const showSkeleton = loading && !data;
  const showError = !!error && !data;

  // Main view surfaces only the worst-compliance apps; the "View More" sheet below has the full list.
  const topApps = [...apps].sort((a, b) => a.compliancePercent - b.compliancePercent).slice(0, TOP_N);

  const columns: Column<ApplicationComplianceSummary>[] = [
    {
      key: "applicationName",
      header: "Application",
      align: "left",
      headerAlign: "left",
      render: (a) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{a.applicationName}</p>
          <p className="text-xs text-muted-foreground">
            {a.sspCount === 0 ? "No SSP published yet" : `${a.sspCount} SSP${a.sspCount === 1 ? "" : "s"}`}
          </p>
        </div>
      ),
    },
    {
      key: "breakdown",
      header: "Satisfied / Not Satisfied / Not Reviewed",
      align: "left",
      headerAlign: "left",
      render: (a) => (
        <div className="min-w-[160px] space-y-1.5">
          <StatusStackBar passed={a.passed} failed={a.failed} notIdentified={a.notIdentified} />
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold" style={{ color: STATUS_HEX.good }}>{a.passed} satisfied</span>
            {" · "}
            <span className="font-semibold" style={{ color: STATUS_HEX.critical }}>{a.failed} not satisfied</span>
            {" · "}
            <span className="font-semibold" style={{ color: NOT_IDENTIFIED_HEX }}>{a.notIdentified} not reviewed</span>
            {" · "}
            {a.coveredControls} of {a.totalControls} total controls
          </p>
        </div>
      ),
    },
    {
      key: "coverage",
      header: "Coverage",
      render: (a) => <span className="text-sm font-semibold tabular-nums text-foreground">{a.coveragePercent.toFixed(1)}%</span>,
    },
    {
      key: "compliance",
      header: "Compliance",
      render: (a) => <CompliancePctBadge pct={a.compliancePercent} />,
    },
  ];

  return (
    <Card className="flex h-full min-h-[425px] flex-col">
      <div className="mb-1 flex items-start justify-between gap-2">
        <SectionTitle
          title="Application Compliance Score"
          subtitle="Provides an overview of the current compliance posture based on the latest assessment outcomes."
        />
        <div className="flex shrink-0 items-center gap-2">
          <Tip
            label={
              <span className="block max-w-[260px]">
                Compliance % = Satisfied ÷ (Satisfied + Not Satisfied) — only controls with a finished assessment count.
                Coverage % = how much of the control baseline has a plan at all. Satisfied + Not Satisfied + Not
                Reviewed always equals the covered control count — Not Reviewed means a plan exists but hasn't
                completed review yet.
              </span>
            }
          >
            <Info className="size-4 shrink-0 text-muted-foreground" />
          </Tip>
          <button
            onClick={() => setViewAllOpen(true)}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-indigo-500/50 px-2.5 py-1 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
          >
            View More <ArrowRight className="size-3.5" />
          </button>
        </div>
      </div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <StatusLegend />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {showSkeleton ? (
          <SkeletonList rows={5} />
        ) : showError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load this widget"
            description={error!}
            action={
              <button
                onClick={load}
                className="rounded-lg border border-indigo-500/50 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
              >
                Retry
              </button>
            }
          />
        ) : (
          <DataTable
            columns={columns}
            data={topApps}
            rowKey={(a) => a.applicationName}
            emptyText="No Data Available"
            onRowClick={(a) => setSelected(a)}
            hidePageButtons
            compact
          />
        )}
      </div>
      <SspDetailSheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)} app={selected} />

      <Sheet open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[96vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1100px]">
          <div className="shrink-0 border-b border-border p-6">
            <h2 className="text-xl font-bold text-foreground">All Applications — Compliance Score</h2>
            <p className="mt-1 text-sm text-muted-foreground">{apps.length} applications. Click a row for its per-SSP breakdown.</p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <StatusLegend />
            <DataTable
              columns={columns}
              data={apps}
              rowKey={(a) => a.applicationName}
              emptyText="No Data Available"
              onRowClick={(a) => {
                setViewAllOpen(false);
                setSelected(a);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}
